<template>
  <gmap-map
    class="google-heat-map"
    ref="heatmap"
    :center="center"
    :zoom="zoom"
    :options="options"
    :map-type-id="mapTypeId"
    @zoom_changed="zoomChanged"
  >
  </gmap-map>
</template>

<script>
export default {
  name: "GoogleHeatMap",

  props: {
    heatMapData: {
      type: Array,
      default: function() {
        return []
      },
    },
    center: {
      type: Object,
      default: function() {
        return { lat: 0, lng: 0 }
      },
    },
    options: {
      type: Object,
      default: function() {
        return {}
      }
    },
    zoom: {
      type: Number,
      default: 3
    },
    mapTypeId: {
      type: String,
      default: 'terrain'
    },
    google: {
      type: Object,
      required: true
    },
    needBound: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      map: null,
      heatMapLayers: [],
      isFitBounding: false,
      palette: null,
    }
  },

  computed: {
    weightedLocations() {
      const groupedPoints = {}

      this.heatMapData.forEach(([lng, lat, weight]) => {
        const parsedLng = Number(lng)
        const parsedLat = Number(lat)

        if (!Number.isFinite(parsedLng) || !Number.isFinite(parsedLat)) {
          return
        }

        const key = `${parsedLng.toFixed(5)},${parsedLat.toFixed(5)}`
        const parsedWeight = Number(weight)
        const pointWeight = Number.isFinite(parsedWeight) ? Math.max(0, parsedWeight) : 1

        if (!groupedPoints[key]) {
          groupedPoints[key] = {
            location: new this.google.maps.LatLng(parsedLat, parsedLng),
            weight: 0
          }
        }

        groupedPoints[key].weight += pointWeight
      })

      const points = Object.values(groupedPoints)

      return points.length ? [points] : []
    },

    maxPointWeight() {
      return this.weightedLocations.reduce((maxWeight, points) => {
        return points.reduce((maxPointWeight, point) => {
          return Math.max(maxPointWeight, point.weight)
        }, maxWeight)
      }, 1)
    }
  },

  watch: {
    weightedLocations() {
      this.initHeatmaps()
    }
  },

  mounted() {
    this.$refs.heatmap.$mapPromise.then(map => {
      this.map = map
      this.$emit('map-promise', map)
      this.initHeatmaps()
    })
  },

  beforeDestroy() {
    this.destroyHeatmaps()
    this.map = null
  },

  methods: {
    zoomChanged(zoom) {
      if (!this.isFitBounding) {
        this.updateIntensity()
        this.$emit('zoom-changed', zoom)
      } else {
        this.isFitBounding = false
      }
    },

    updateIntensity() {
      this.heatMapLayers.forEach(layer => layer.draw())
    },

    getMaxIntensity() {
      return this.maxPointWeight
    },

    createPalette() {
      const canvas = document.createElement('canvas')
      canvas.width = 256
      canvas.height = 1

      const context = canvas.getContext('2d')
      const gradient = context.createLinearGradient(0, 0, 256, 0)
      gradient.addColorStop(0, '#0000ff')
      gradient.addColorStop(0.25, '#00ffff')
      gradient.addColorStop(0.5, '#00ff00')
      gradient.addColorStop(0.75, '#ffff00')
      gradient.addColorStop(1, '#ff0000')
      context.fillStyle = gradient
      context.fillRect(0, 0, 256, 1)

      return context.getImageData(0, 0, 256, 1).data
    },

    createDensityOverlay(points) {
      const component = this
      const overlay = new this.google.maps.OverlayView()

      overlay.onAdd = function() {
        this.container = document.createElement('div')
        this.container.style.position = 'absolute'
        this.container.style.pointerEvents = 'none'

        this.canvas = document.createElement('canvas')
        this.canvas.style.position = 'absolute'
        this.canvas.style.left = '0'
        this.canvas.style.top = '0'
        this.container.appendChild(this.canvas)
        this.getPanes().overlayLayer.appendChild(this.container)
      }

      overlay.draw = function() {
        component.drawDensityOverlay(this, points)
      }

      overlay.onRemove = function() {
        if (this.container && this.container.parentNode) {
          this.container.parentNode.removeChild(this.container)
        }
        this.container = null
        this.canvas = null
      }

      overlay.setMap(this.map)
      return overlay
    },

    drawDensityOverlay(overlay, points) {
      if (!overlay.canvas || !this.map || !this.map.getBounds()) {
        return
      }

      const projection = overlay.getProjection()
      const bounds = this.map.getBounds()
      const northEast = projection.fromLatLngToDivPixel(bounds.getNorthEast())
      const southWest = projection.fromLatLngToDivPixel(bounds.getSouthWest())
      const width = Math.ceil(northEast.x - southWest.x)
      const height = Math.ceil(southWest.y - northEast.y)

      if (width <= 0 || height <= 0) {
        return
      }

      overlay.container.style.left = `${southWest.x}px`
      overlay.container.style.top = `${northEast.y}px`
      overlay.container.style.width = `${width}px`
      overlay.container.style.height = `${height}px`

      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2)
      const canvasWidth = Math.max(1, Math.ceil(width * pixelRatio))
      const canvasHeight = Math.max(1, Math.ceil(height * pixelRatio))
      const canvas = overlay.canvas
      canvas.width = canvasWidth
      canvas.height = canvasHeight
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`

      const context = canvas.getContext('2d')
      const radius = 3 * pixelRatio
      const brush = document.createElement('canvas')
      brush.width = Math.ceil(radius * 2)
      brush.height = Math.ceil(radius * 2)

      const brushContext = brush.getContext('2d')
      const brushGradient = brushContext.createRadialGradient(radius, radius, 0, radius, radius, radius)
      brushGradient.addColorStop(0, 'rgba(0, 0, 0, 1)')
      brushGradient.addColorStop(1, 'rgba(0, 0, 0, 0)')
      brushContext.fillStyle = brushGradient
      brushContext.fillRect(0, 0, brush.width, brush.height)

      const maxIntensity = this.getMaxIntensity()
      context.clearRect(0, 0, canvasWidth, canvasHeight)
      context.globalCompositeOperation = 'lighter'

      points.forEach(point => {
        const pixel = projection.fromLatLngToDivPixel(point.location)
        const x = (pixel.x - southWest.x) * pixelRatio
        const y = (pixel.y - northEast.y) * pixelRatio

        if (x < -radius || x > canvasWidth + radius || y < -radius || y > canvasHeight + radius) {
          return
        }

        context.globalAlpha = Math.min(1, point.weight / maxIntensity)
        context.drawImage(brush, x - radius, y - radius)
      })

      context.globalAlpha = 1
      context.globalCompositeOperation = 'source-over'

      const image = context.getImageData(0, 0, canvasWidth, canvasHeight)
      const pixels = image.data
      const palette = this.palette || (this.palette = this.createPalette())

      for (let index = 0; index < pixels.length; index += 4) {
        const alpha = pixels[index + 3]

        if (!alpha) {
          continue
        }

        const colorIndex = alpha * 4
        pixels[index] = palette[colorIndex]
        pixels[index + 1] = palette[colorIndex + 1]
        pixels[index + 2] = palette[colorIndex + 2]
        pixels[index + 3] = Math.min(230, alpha)
      }

      context.putImageData(image, 0, 0)
    },

    destroyHeatmaps() {
      this.heatMapLayers.forEach(layer => layer.setMap(null))
      this.heatMapLayers = []
    },

    initHeatmaps() {
      this.destroyHeatmaps()

      if (!this.map) {
        return
      }

      this.heatMapLayers = this.weightedLocations.map(points => {
        return this.createDensityOverlay(points)
      })

      if (this.needBound) {
        this.fitBounds()
      }

      this.updateIntensity()
    },

    fitBounds() {
      if (!this.weightedLocations.length) {
        return
      }

      this.isFitBounding = true
      const bounds = new this.google.maps.LatLngBounds()

      this.weightedLocations.forEach(points => {
        points.forEach(point => bounds.extend(point.location))
      })
      this.map.fitBounds(bounds)
    }
  }
};
</script>

<style scoped>
.google-heat-map {
  width: 100%;
  height: 100%;
}
</style>
